/**
 * The copy the Watchtower nudges build off a DECODED report (2026-09-24).
 *
 * Three surfaces read from here: the launch popup names the first pick, the
 * report page hands the picks to Deck Chat, and the deck page reads that
 * hand-off back out of navigation state. Every function takes a report that
 * already passed `parseReport` and still reads each field defensively, because
 * `parseReport` is a structural gate, not a field-by-field one: a pick without
 * a readable name produces no sentence rather than "undefined fits your deck".
 *
 * Nothing here invents a card name. Every name comes off the report.
 *
 * Localization (Phase 3, 3g). The popup's pick sentence is display copy, in
 * `common:watchtower_popup` (the popup is mounted eagerly at the app root, so
 * it cannot read the `watchtower` namespace). The Deck Chat hand-off DRAFT is
 * frozen English wire text and lives in ./watchtowerChatDraft, re-exported
 * here; the hand-off BUTTON label is the `watchtower` catalog, in
 * components/watchtower/reportBits.tsx, so this module (which the root popup
 * imports) never pulls that catalog into the eager chunk.
 */
import i18n from '../i18n'
import type { WatchtowerReport } from './watchtower'

export { chatHandoffDraft, joinPickNames } from './watchtowerChatDraft'

function nonBlank(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * The deck as the popup names it: the deck name, else the command zone joined
 * with " + ", else null (and the popup keeps its generic body).
 */
export function reportDeckLabel(report: WatchtowerReport): string | null {
  const header = report.header as Partial<WatchtowerReport['header']> | undefined
  const name = nonBlank(header?.deck_name)
  if (name) return name
  const commanders = Array.isArray(header?.commander_names)
    ? header.commander_names.map(nonBlank).filter((n): n is string => n !== null)
    : []
  return commanders.length > 0 ? commanders.join(' + ') : null
}

/**
 * The popup's pick sentence, or null when the generic body should stay:
 * zero picks, an unreadable first name, or no deck label.
 */
export function popupPickSentence(report: WatchtowerReport): string | null {
  const picks = Array.isArray(report.picks) ? report.picks : []
  if (picks.length === 0) return null
  const first = nonBlank((picks[0] as { name?: unknown } | undefined)?.name)
  if (!first) return null
  const deck = reportDeckLabel(report)
  if (!deck) return null
  const rest = picks.length - 1
  if (rest === 0) return i18n.t('common:watchtower_popup.pick_single', { pick: first, deck })
  return i18n.t('common:watchtower_popup.pick_more', { pick: first, deck, count: rest })
}

/** Every pick's name, in report order, skipping any the body does not carry. */
export function handoffPickNames(report: WatchtowerReport): string[] {
  const picks = Array.isArray(report.picks) ? report.picks : []
  return picks
    .map(p => nonBlank((p as { name?: unknown } | undefined)?.name))
    .filter((n): n is string => n !== null)
}

/**
 * The react-router navigation-state key the report page writes the draft
 * under. State, never a URL param: a URL can be crafted and shared, and a
 * crafted link must not be able to pre-fill someone's composer.
 */
export const WATCHTOWER_CHAT_DRAFT_STATE_KEY = 'watchtowerChatDraft'

/**
 * The hand-off as navigation state. ADDRESSED, not a bare string: the user who
 * pressed the button and the deck it was written about. The deck page places
 * it only when both still match (the current session, the route's deck), so
 * an account switch between the two pages can never put A's text in B's
 * composer, and a draft never lands on a deck it was not about.
 */
export interface WatchtowerChatDraftState {
  text: string
  ownerId: string
  deckId: string
}

export function watchtowerChatDraftState(draft: WatchtowerChatDraftState): Record<string, unknown> {
  return { [WATCHTOWER_CHAT_DRAFT_STATE_KEY]: draft }
}

/** True when `location.state` carries our key at all, well-formed or not, so
 *  the consumer knows there is something to clear. */
export function hasWatchtowerChatState(state: unknown): boolean {
  return !!state && typeof state === 'object' && WATCHTOWER_CHAT_DRAFT_STATE_KEY in state
}

/** The addressed draft out of `location.state`, or null for anything else
 *  (including the old bare-string shape, which carries no address). */
export function readWatchtowerChatDraft(state: unknown): WatchtowerChatDraftState | null {
  if (!state || typeof state !== 'object') return null
  const raw = (state as Record<string, unknown>)[WATCHTOWER_CHAT_DRAFT_STATE_KEY]
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const text = nonBlank(r.text)
  const ownerId = nonBlank(r.ownerId)
  const deckId = nonBlank(r.deckId)
  return text && ownerId && deckId ? { text, ownerId, deckId } : null
}
